import { setHeaderGlassGradient } from './header-glass-shape.js';

export const initHeaderDropdowns = () => {
    const root = document.querySelector('[data-header-dropdown-root]');
    const triggers = Array.from(document.querySelectorAll('[data-header-dropdown-trigger]'));
    if (!root || !triggers.length || root.dataset.initialized) return;
    root.dataset.initialized = 'true';

    const desktop = window.matchMedia('(min-width: 1025px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let active = null;
    let closeTimer = 0;

    const cancelClose = () => window.clearTimeout(closeTimer);
    const restore = (entry) => {
        window.cancelAnimationFrame(entry.frame);
        const { trigger, menu, marker, nav, placeholder, surface, outlineLayer, navStyle } = entry;
        const focusedNavItem = nav.contains(document.activeElement) ? document.activeElement : null;
        if (active === entry) active = null;
        trigger.setAttribute('aria-expanded', 'false');
        menu.classList.remove('header__navbar-submenu--open', 'header__navbar-submenu--closing');
        menu.inert = false;
        marker.parentNode.insertBefore(menu, marker.nextSibling);
        menu.removeAttribute('style');
        placeholder.replaceWith(nav);
        nav.classList.remove('header__navbar--dropdown-open');
        if (navStyle === null) nav.removeAttribute('style');
        else nav.setAttribute('style', navStyle);
        surface.remove();
        outlineLayer.remove();
        if (focusedNavItem) focusedNavItem.focus({ preventScroll: true });
    };
    const animate = (entry, target, complete) => {
        window.cancelAnimationFrame(entry.frame);
        const from = entry.expansion;
        const startTime = performance.now();
        const duration = target ? 400 : 240;
        const tick = (now) => {
            if (active !== entry) return;
            const time = reducedMotion.matches ? 1 : Math.min(1, (now - startTime) / duration);
            // A small overshoot makes the growing glass settle like a drop.
            const eased = target
                ? 1 + 2 * Math.pow(time - 1, 3) + Math.pow(time - 1, 2)
                : 1 - Math.pow(1 - time, 3);
            entry.expansion = time === 1 ? target : from + (target - from) * eased;
            renderShape(entry);
            if (time < 1) entry.frame = window.requestAnimationFrame(tick);
            else if (complete) complete();
        };
        if (reducedMotion.matches) tick(startTime);
        else entry.frame = window.requestAnimationFrame(tick);
    };
    const close = (immediate = false) => {
        cancelClose();
        if (!active) return;
        const entry = active;
        if (immediate === true || reducedMotion.matches) {
            restore(entry);
            return;
        }
        if (entry.closing) return;
        entry.closing = true;
        entry.trigger.setAttribute('aria-expanded', 'false');
        const textStyle = window.getComputedStyle(entry.menu);
        entry.menu.style.setProperty('--dropdown-close-opacity', textStyle.opacity);
        entry.menu.style.setProperty('--dropdown-close-transform', textStyle.transform);
        entry.menu.classList.add('header__navbar-submenu--closing');
        entry.menu.inert = true;
        animate(entry, 0, () => restore(entry));
    };
    const scheduleClose = () => {
        cancelClose();
        closeTimer = window.setTimeout(() => {
            if (active && (active.menu.contains(document.activeElement) || active.trigger === document.activeElement)) return;
            close();
        }, 150);
    };
    const position = () => {
        if (!active) return;
        const { trigger, menu, nav, surface, outlineLayer, navRect } = active;
        const width = document.documentElement.clientWidth;
        const rtl = window.getComputedStyle(trigger).direction === 'rtl';
        const inlineOffset = (x, itemWidth) => (rtl ? width - x - itemWidth : x) + 'px';
        nav.style.direction = rtl ? 'rtl' : 'ltr';
        nav.style.insetInlineStart = inlineOffset(navRect.left, navRect.width);
        nav.style.top = navRect.top + 'px';
        nav.style.width = navRect.width + 'px';
        const rect = trigger.getBoundingClientRect();
        menu.style.direction = rtl ? 'rtl' : 'ltr';
        menu.style.maxWidth = Math.min(300, navRect.width) + 'px';
        menu.style.minWidth = Math.min(150, navRect.width) + 'px';
        menu.style.maxHeight = Math.max(0, window.innerHeight - navRect.bottom - 12) + 'px';
        const menuWidth = menu.offsetWidth;
        let x = Math.max(navRect.left, Math.min(rect.left + (rect.width - menuWidth) / 2, navRect.right - menuWidth));
        // A narrow shoulder cannot fit the rounded glass junction and looks clipped.
        // Join nearby edges instead, keeping the menu and its glass on the same bounds.
        const junctionRadius = 20;
        if (x - navRect.left < junctionRadius) x = navRect.left;
        else if (navRect.right - x - menuWidth < junctionRadius) x = navRect.right - menuWidth;
        menu.style.top = navRect.bottom + 'px';
        menu.style.insetInlineStart = inlineOffset(x, menuWidth);

        active.geometry = {
            w: navRect.width,
            h: navRect.height,
            menuHeight: menu.offsetHeight,
            start: x - navRect.left,
            end: x - navRect.left + menuWidth,
        };
        surface.style.top = navRect.top + 'px';
        surface.style.insetInlineStart = inlineOffset(navRect.left, navRect.width);
        surface.style.direction = rtl ? 'rtl' : 'ltr';
        surface.style.width = navRect.width + 'px';
        setHeaderGlassGradient(outlineLayer, nav, navRect.width, navRect.height, menu.offsetHeight, navRect.width, 0, '--color-white-20');

        // Leave room for the small overshoot without clipping the bottom edge.
        surface.style.height = (navRect.height + menu.offsetHeight * 1.04) + 'px';
        outlineLayer.style.top = surface.style.top;
        outlineLayer.style.direction = surface.style.direction;
        outlineLayer.style.insetInlineStart = surface.style.insetInlineStart;
        outlineLayer.style.width = surface.style.width;
        outlineLayer.style.height = surface.style.height;
        renderShape(active);
    };
    const renderShape = (entry) => {
        const { surface, outlineLayer = surface, geometry, expansion } = entry;
        const { w, h, menuHeight, start, end } = geometry;
        const bottom = h + menuHeight * Math.max(0, expansion);
        const buildPath = (inset) => {
            let points = [[inset, inset], [w - inset, inset], [w - inset, h - inset]];
            if (expansion > 0) {
                if (end < w - 1) points.push([end - inset, h - inset]);
                points.push([end - inset, bottom - inset], [start + inset, bottom - inset]);
                if (start > 1) points.push([start + inset, h - inset]);
            }
            points.push([inset, h - inset]);
            const corners = points.filter((point, index) => {
                const prev = points[(index + points.length - 1) % points.length];
                const next = points[(index + 1) % points.length];
                return (point[0] - prev[0]) * (next[1] - point[1]) !== (point[1] - prev[1]) * (next[0] - point[0]);
            }).map((point, index, vertices) => {
                const prev = vertices[(index + vertices.length - 1) % vertices.length];
                const next = vertices[(index + 1) % vertices.length];
                const before = Math.hypot(prev[0] - point[0], prev[1] - point[1]);
                const after = Math.hypot(next[0] - point[0], next[1] - point[1]);
                const roundness = point[1] > h ? 20 + 32 * Math.max(0, 1 - expansion) : 20;
                const radius = Math.min(roundness, before / 2, after / 2);
                const along = (target, distance) => point.map((value, axis) => value + (target[axis] - value) * radius / distance);
                const turn = (point[0] - prev[0]) * (next[1] - point[1]) - (point[1] - prev[1]) * (next[0] - point[0]);
                return { radius, sweep: turn > 0 ? 1 : 0, enter: along(prev, before), exit: along(next, after) };
            });
            return corners.map((corner, index) => {
                return (index ? 'L' : 'M') + corner.enter.join(' ') + 'A' + corner.radius + ' ' + corner.radius + ' 0 0 ' + corner.sweep + ' ' + corner.exit.join(' ');
            }).join(' ') + 'Z';
        };
        const path = buildPath(0.5);
        const height = h + menuHeight * 1.04;
        const outline = '<svg xmlns="http://www.w3.org/2000/svg" width="' + w + '" height="' + height + '" viewBox="0 0 ' + w + ' ' + height + '"><path d="' + path + '" fill="none" stroke="white" stroke-width="1"/></svg>';
        surface.style.setProperty('--dropdown-shape', 'path("' + buildPath(0) + '")');
        outlineLayer.style.setProperty('--dropdown-outline', 'url("data:image/svg+xml,' + encodeURIComponent(outline) + '")');
    };
    const open = (entry) => {
        cancelClose();
        if (!desktop.matches) return;
        if (active !== entry) {
            close(true);
            const nav = entry.trigger.closest('.header__navbar');
            const rect = nav.getBoundingClientRect();
            const placeholder = document.createElement('span');
            placeholder.className = 'header-dropdown-placeholder';
            placeholder.setAttribute('aria-hidden', 'true');
            placeholder.style.width = rect.width + 'px';
            placeholder.style.height = rect.height + 'px';
            const surface = document.createElement('div');
            surface.className = 'header-dropdown-surface';
            surface.setAttribute('aria-hidden', 'true');
            entry.nav = nav;
            entry.navRect = rect;
            entry.navStyle = nav.getAttribute('style');
            entry.placeholder = placeholder;
            const outlineLayer = document.createElement('div');
            outlineLayer.className = 'header-dropdown-outline';
            outlineLayer.setAttribute('aria-hidden', 'true');
            entry.outlineLayer = outlineLayer;
            entry.surface = surface;
            entry.expansion = 0;
            entry.closing = false;
            active = entry;
            nav.replaceWith(placeholder);
            nav.classList.add('header__navbar--dropdown-open');
            root.append(surface, outlineLayer, nav, entry.menu);
            entry.menu.classList.add('header__navbar-submenu--open');
            entry.trigger.setAttribute('aria-expanded', 'true');
            position();
            animate(entry, 1);
        } else if (entry.closing) {
            entry.closing = false;
            entry.menu.inert = false;
            entry.menu.classList.remove('header__navbar-submenu--closing');
            entry.trigger.setAttribute('aria-expanded', 'true');
            animate(entry, 1);
        }
    };

    // Use the same SVG gradient and one-pixel stroke for both navbar states.
    const navbars = new Set(triggers.map((trigger) => trigger.closest('.header__navbar')));
    navbars.forEach((nav) => {
        if (!nav) return;
        const syncBorder = () => {
            if (nav.classList.contains('header__navbar--dropdown-open')) return;
            const rect = nav.getBoundingClientRect();
            if (!rect.width || !rect.height) return;
            setHeaderGlassGradient(nav, nav, rect.width, rect.height, 0, rect.width, 0, '--color-white-20');
            renderShape({
                surface: nav,
                geometry: { w: rect.width, h: rect.height, menuHeight: 0, start: 0, end: 0 },
                expansion: 0,
            });
            nav.classList.add('header__navbar--border-ready');
        };
        syncBorder();
        const observer = new ResizeObserver(syncBorder);
        observer.observe(nav);
    });

    triggers.forEach((trigger, index) => {
        const menu = trigger.nextElementSibling;
        if (!menu || !menu.classList.contains('header__navbar-submenu')) return;
        const marker = document.createComment('header dropdown position');
        menu.parentNode.insertBefore(marker, menu);
        menu.id = menu.id || 'header-dropdown-' + index;
        trigger.setAttribute('aria-controls', menu.id);
        const entry = { trigger, menu, marker };
        const links = Array.from(menu.querySelectorAll('a[href]'));
        trigger.parentNode.addEventListener('mouseenter', () => open(entry));
        trigger.parentNode.addEventListener('mouseleave', scheduleClose);
        menu.addEventListener('mouseenter', cancelClose);
        menu.addEventListener('mouseleave', scheduleClose);
        trigger.addEventListener('click', () => {
            // A parent item with a link navigates on click; its dropdown opens on hover.
            if (trigger.tagName === 'A') return;
            open(entry);
            if (links.length) links[0].focus();
        });
        trigger.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey && active === entry)) {
                if (!links.length) return;
                event.preventDefault();
                open(entry);
                links[0].focus();
            }
        });
        menu.addEventListener('keydown', (event) => {
            if (event.key !== 'Tab') return;
            if (event.shiftKey && document.activeElement === links[0]) {
                event.preventDefault();
                trigger.focus();
                close(true);
            } else if (!event.shiftKey && document.activeElement === links[links.length - 1]) {
                // Restore the original DOM order before the browser advances focus.
                trigger.focus();
                close(true);
            }
        });
        menu.addEventListener('click', (event) => {
            if (event.target.closest('a')) close();
        });
    });

    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !active) return;
        const trigger = active.trigger;
        close();
        trigger.focus();
    });
    document.addEventListener('mousedown', (event) => {
        if (active && !active.menu.contains(event.target) && !active.trigger.contains(event.target)) close();
    });
    document.addEventListener('focusin', (event) => {
        if (active && !active.closing && !active.menu.contains(event.target) && event.target !== active.trigger) close();
    });
    window.addEventListener('scroll', (event) => {
        if (active && !active.menu.contains(event.target)) close(true);
    }, true);
    window.addEventListener('resize', () => close(true));
    reducedMotion.addEventListener('change', () => close(true));
};
